import { useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileText } from "lucide-react";

export interface PreviewAttachment {
  file_name: string;
  file_type: string | null;
  signed_url: string;
}

interface Props {
  attachment: PreviewAttachment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  total?: number;
  index?: number;
  onPrev?: () => void;
  onNext?: () => void;
}

export const isPreviewable = (fileType: string | null | undefined, fileName?: string) => {
  if (fileType?.startsWith("image/")) return true;
  if (fileType === "application/pdf") return true;
  if (fileName?.toLowerCase().endsWith(".pdf")) return true;
  return false;
};

const SWIPE_THRESHOLD = 50;

export const AttachmentPreviewModal = ({
  attachment,
  open,
  onOpenChange,
  total = 0,
  index = 0,
  onPrev,
  onNext,
}: Props) => {
  const touchStartX = useRef<number | null>(null);

  // Reset touch state whenever the slide changes
  useEffect(() => {
    touchStartX.current = null;
  }, [attachment]);

  if (!attachment) return null;

  const isImage = attachment.file_type?.startsWith("image/");
  const isPdf =
    attachment.file_type === "application/pdf" ||
    attachment.file_name.toLowerCase().endsWith(".pdf");
  const hasNav = total > 1 && (onPrev || onNext);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = attachment.signed_url;
    a.download = attachment.file_name;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (dx < 0) onNext?.();
    else onPrev?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base font-medium pr-8 truncate">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{attachment.file_name}</span>
            {total > 1 && (
              <span className="ml-auto pl-2 text-xs font-normal text-muted-foreground shrink-0">
                {index + 1} / {total}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div
          className="relative flex-1 overflow-auto bg-muted/30 flex items-center justify-center min-h-0"
          onTouchStart={hasNav ? onTouchStart : undefined}
          onTouchEnd={hasNav ? onTouchEnd : undefined}
        >
          {isImage ? (
            <img
              src={attachment.signed_url}
              alt={attachment.file_name}
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
            />
          ) : isPdf ? (
            <iframe
              src={attachment.signed_url}
              title={attachment.file_name}
              className="w-full h-full border-0 bg-background"
            />
          ) : (
            <div className="text-center p-8 text-muted-foreground text-sm">
              Preview not available for this file type.
            </div>
          )}

          {hasNav && (
            <>
              <button
                type="button"
                aria-label="Previous attachment"
                onClick={onPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/80 hover:bg-background shadow border flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Next attachment"
                onClick={onNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/80 hover:bg-background shadow border flex items-center justify-center transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t flex flex-wrap items-center justify-end gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(attachment.signed_url, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Open in new tab
          </Button>
          <Button size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
